"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-6 bg-card/50 border border-border rounded-xl animate-in fade-in zoom-in duration-500">
        <div className="rounded-full bg-primary/20 p-4">
          <CircleCheck className="size-12 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">Quote Requested!</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 text-primary hover:bg-primary/10 hover:text-primary">
          Request Another Quote
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Full Name <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background/50 border-input/50 focus-visible:border-primary focus-visible:ring-primary/20 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Email Address <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="coastalprestigemowing@gmail.com" className="bg-background/50 border-input/50 focus-visible:border-primary focus-visible:ring-primary/20 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Phone Number <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0410 147 510" className="bg-background/50 border-input/50 focus-visible:border-primary focus-visible:ring-primary/20 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="service_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Service Required <span className="text-primary">*</span></FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background/50 border-input/50 focus:border-primary focus:ring-primary/20 h-12">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {["Lawn Mowing", "Lawn Edging", "Garden Maintenance", "Hedge Trimming", "Property Maintenance", "Strata & Commercial Maintenance", "Green Waste Removal"].map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="property_address" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Property Address</FormLabel>
            <FormControl>
              <Input placeholder="123 Main St, Central Coast NSW" className="bg-background/50 border-input/50 focus-visible:border-primary focus-visible:ring-primary/20 h-12" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Additional Details</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[120px] w-full rounded-md border border-input/50 bg-background/50 px-3 py-3 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm placeholder:text-muted-foreground" placeholder="Any special instructions or questions?" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3 text-destructive animate-in fade-in slide-in-from-top-2">
          <CircleAlert className="size-5 shrink-0" />
          <p className="text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 transition-all duration-300 hover:scale-[1.01]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Request...
          </> : <>
            Get My Free Quote
            <Send className="ml-2 size-5" />
          </>}
      </Button>
      
      <p className="text-center text-xs text-muted-foreground mt-4">
        By submitting this form, you agree to our privacy policy. We respect your data.
      </p>
    </Form>;
}
export default function GetYourQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="get-your-quote" className="dark bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Let's get your property <span className="text-primary">prestige-ready</span>.
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Fill out the form below for a fast, free quote. We service residential, strata, and commercial properties across the Central Coast.
            </p>
          </div>

          <div className="bg-card border border-border/50 rounded-2xl shadow-2xl shadow-black/20 p-6 md:p-10 backdrop-blur-sm">
            <FormProvider formId="quote_form_contact" sectionName="get-your-quote" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            service_type: "",
            property_address: "",
            additional_details: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            service_type: {
              required: true
            }
          }} submitText="Get My Free Quote" successMessage="Thank you for your interest! We have received your quote request and will be in touch shortly to discuss your property needs." errorMessage="We couldn't submit your request. Please check your connection and try again.">
              <QuoteForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}